import React, {
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useWatch } from 'react-hook-form';
import type { AuditDynamicFormRHFNewProps } from '../../types/auditFormTypes';
import AuditDynamicFormSync from './AuditDynamicFormSync';
import AuditDynamicFormTotals from './AuditDynamicFormTotals';
import AuditDynamicFormTicketRow from './AuditDynamicFormTicketRow';
import AuditDynamicFormTableHeader from './AuditDynamicFormTableHeader';
import AuditDynamicFormHeader from './AuditDynamicFormHeader';
import AuditDynamicFormTopBar from './AuditDynamicFormTopBar';
import AuditDynamicFormActions from './AuditDynamicFormActions';
import AuditDynamicFormErrorDialog from './AuditDynamicFormErrorDialog';
import useAuditDynamicFormLogic from '../../features/audit/fixOcr/hooks/useAuditDynamicFormLogic';

const AuditDynamicFormRHFNew = forwardRef<any, AuditDynamicFormRHFNewProps>(
  (
    {
      initialValues,
      onSubmit,
      isSubmitting,
      savingAction,
      auditStatus,
      ticketDetails = [],
      onFormDataUpdate,
      onSwitchModes,
      gamesData,
      hoveredRowIndex,
      onRowHover,
      storeName,
      onRevertToReady,
      varianceByGameId,
      onRefreshSimulation,
      simulationLoading,
      simulationError,
      showTopBar = true,
      showConfidenceChip = true,
      showJsonToggle = true,
      showRejectAction = true,
      showRevertAction = true,
      submitButtonLabel = 'Process',
    },
    ref
  ) => {
    const {
      control,
      handleSubmit,
      errors,
      setValue,
      getValues,
      fields,
      errorPopupOpen,
      errorMessage,
      handleAppend,
      handleInsert,
      handleRemove,
      handleSwitchToJson,
      handleReject,
      handleCloseErrorDialog,
      handleFormSubmit,
      handleSubmitError,
    } = useAuditDynamicFormLogic({
      initialValues,
      onSubmit,
      onSwitchModes,
    });

    useImperativeHandle(ref, () => ({
      getValues,
      setValue,
    }), [getValues, setValue]);

    const watchedTicketDetails = useWatch({
      control,
      name: 'ticket_details',
    });

    const effectiveTicketDetails = Array.isArray(watchedTicketDetails) ? watchedTicketDetails : ticketDetails;

    // if (ticketDetails.length == 0) {
    //   return (
    //     <Box display="flex" justifyContent="center" alignItems="center">
    //       <Paper sx={{ p: 2, width: '100%', maxWidth: 1200, textAlign: 'center' }}>
    //         <Typography variant="body1" fontWeight={600} fontSize={24}>No Processed Data Found</Typography>
    //       </Paper>
    //     </Box>
    //   );
    // }

    return (
      <form onSubmit={handleSubmit(handleFormSubmit, handleSubmitError)}>
        <AuditDynamicFormSync
          control={control}
          getValues={getValues}
          onFormDataUpdate={onFormDataUpdate}
        />

        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
        </style>

        {showTopBar && (
          <AuditDynamicFormTopBar
            storeName={storeName}
            initialValues={initialValues}
            onSwitchToJson={handleSwitchToJson}
            showConfidenceChip={showConfidenceChip}
            showJsonToggle={showJsonToggle}
          />
        )}

        <AuditDynamicFormHeader
          control={control}
          errors={errors}
          retailerLocked={!!initialValues?.retailer_from_store}
        />

        <Box mt={0.5}>
          <Box sx={{ mt: 0, border: "1px solid grey", borderRadius: "8px", px: 1 }}>
            <AuditDynamicFormTableHeader
              onRefreshSimulation={onRefreshSimulation}
              simulationLoading={simulationLoading}
              simulationError={simulationError}
            />

            {/* Ticket Rows */}
            {fields.map((field: any, idx: number) => {
              const ticket = effectiveTicketDetails[idx];
              const rowErrors: any =
                ((errors?.ticket_details as unknown as any[]) || [])[idx] || {};

              return (
                <AuditDynamicFormTicketRow
                  key={`ticket-row-${field.id}-${idx}`}
                  field={field}
                  idx={idx}
                  ticket={ticket}
                  control={control}
                  rowErrors={rowErrors}
                  setValue={setValue}
                  remove={handleRemove}
                  onInsert={handleInsert}
                  varianceByGameId={varianceByGameId}
                  hoveredRowIndex={hoveredRowIndex}
                  onRowHover={onRowHover}
                />
              );
            })}

            {/* Divider with plus icon at the start */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#fff",
                cursor: "pointer",
                opacity: 1,
              }}
            >
              <Box
                className="add-btn"
                sx={{
                  width: "calc( 100% - 20px )",
                  height: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ p: 0, width: 20, height: 20 }}
                  onClick={handleAppend}
                >
                  <Add sx={{ fontSize: 20 }} />
                </IconButton>
                <Divider
                  sx={{
                    borderColor:
                      hoveredRowIndex === fields.length ? "#000000" : "#A3AED099",
                    borderWidth:
                      hoveredRowIndex === fields.length ? "0.5px" : "0.5px",
                    borderStyle: "solid",
                    width: "100%",
                    transition: "all 0.1s ease-in-out",
                    boxShadow:
                      hoveredRowIndex === fields.length
                        ? "0 0 8px #FF6B3540"
                        : "none",
                  }}
                />


              </Box>

            </Box>
            <AuditDynamicFormTotals control={control} gamesData={gamesData} />
          </Box>
        </Box>



        <AuditDynamicFormActions
          isSubmitting={isSubmitting}
          savingAction={savingAction}
          auditStatus={auditStatus}
          onReject={handleReject}
          onRevertToReady={onRevertToReady}
          showRejectAction={showRejectAction}
          showRevertAction={showRevertAction}
          submitButtonLabel={submitButtonLabel}
        />

        <AuditDynamicFormErrorDialog
          open={errorPopupOpen}
          errorMessage={errorMessage}
          onClose={handleCloseErrorDialog}
        />
      </form>
    );
  }
);

export default AuditDynamicFormRHFNew;
